<script setup lang="ts">
import type { RouteLocationRaw } from 'vue-router'
import type { TeacherArticle } from '~/models/Content/TeacherArticle'
import { useI18n } from 'vue-i18n'
import { computed, onBeforeUnmount, onMounted, ref, toRef, watch } from 'vue'
import { waitFor } from '~/utils/asyncUtils'
import useProductStore from '~/stores/product'
import useTeacherGuide from '~/composables/useTeacherGuide'
import useSlugify from '~/composables/useSlugify'
import { useScrollBehavior } from '~/composables/useScrollBehavior'
import useGradeString from '~/composables/useGradeString'
import { useContentHelper } from '~/composables/useContentHelper'
import TopBar from '~/components/utils/TopBar.vue'
import RichTextRenderer from '~/components/utils/RichTextRenderer.vue'
import TeacherSubitems from '~/components/subject/TeacherSubitems.vue'
import ArticleContentSkeleton from '~/components/skeletons/ArticleContentSkeleton.vue'

const props = defineProps<{
  locations: TeacherArticle[]
  closeOverride?: RouteLocationRaw
}>()

useScrollBehavior()

const menu = ref<HTMLElement>()
const anchorOffsetPx = ref(0)

const { t } = useI18n()
const { getProductFromResource } = useProductStore()
const { getTitle, isPackage, isArticleContent } = useContentHelper()
const { slugify } = useSlugify()
const { createGradeString } = useGradeString()

const article = computed(() => props.locations[0])

const { teacherContent, isLoadingTeacherContent } = useTeacherGuide({
  contentId: computed(() => article.value.contentId),
  enabled: toRef(() => Boolean(article.value)),
  withContent: true,
})

const articleGrades = computed(() => {
  if (!article.value)
    return ''
  return createGradeString(article.value.grades)
})

const articleSubject = computed(() => {
  if (!article.value)
    return ''
  const product = getProductFromResource(article.value)
  if (product?.subjects.length !== 1)
    return ''
  return t(`metadata.subjects.${product.subjects.at(0)}`)
})

const menuItems = computed(() => {
  if (!article.value)
    return []
  return [
    article.value,
    ...(teacherContent.value ? teacherContent.value : [])
  ].map((articleContent) => {
    const title = getTitle(articleContent)
    return {
      title,
      id: slugify(title),
    }
  })
})

const calculateAnchorOffset = () => {
  anchorOffsetPx.value = window.matchMedia('(min-width: 768px)').matches
    ? 0
    : (menu.value?.offsetHeight ?? 0)
}

function scrollToCurrentHash() {
  const id = decodeURIComponent(location.hash.slice(1))
  document.getElementById(id)?.scrollIntoView()
}

watch(teacherContent, async () => {
  scrollToCurrentHash()
}, { flush: 'post' })

onMounted(async () => {
  await waitFor(() => !!menu.value, 1000)
  calculateAnchorOffset()
  window.addEventListener('resize', calculateAnchorOffset)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', calculateAnchorOffset)
})
</script>

<template>
  <div
    v-if="article"
    class="flex min-h-full flex-col text-blue-50 md:grid md:grid-cols-12"
    :style="{
      '--card-bg': 'var(--color-orange-10)',
      '--card-text': 'var(--color-blue-50)',
      '--card-hover': 'var(--color-orange-20)',
      '--card-border': 'currentColor',
      '--card-accent': 'currentColor',
    }"
  >
    <TopBar
      :item="article"
      :fullscreen="false"
      :favorite="false"
    />
    <ul
      ref="menu"
      class="bg-orange-10 sticky top-0 col-span-4 flex max-h-screen w-full flex-wrap gap-2 overflow-y-auto p-5 pr-16 md:top-16 md:max-h-[calc(100vh-4rem)] md:w-fit md:flex-col md:flex-nowrap md:gap-4 md:bg-transparent md:py-0 md:pr-0 md:pb-16 md:pl-4 lg:col-span-3 xl:pl-10"
    >
      <li
        v-for="menuItem in menuItems"
        :key="menuItem.id"
      >
        <a
          class="bg-orange-5 font-inter hover:bg-orange-30/40 inline-block rounded-full px-3 py-2 text-sm font-medium transition-all focus-visible:ring md:px-5 xl:text-base"
          :href="`#${menuItem.id}`"
          v-text="menuItem.title"
        />
      </li>
    </ul>
    <div class="col-span-8 lg:col-span-9">
      <header
        :id="slugify(getTitle(article))"
        class="au-teacher-article bg-orange-5 fluid-text-lg p-5 md:mx-5 md:mt-16 md:p-8"
        :style="{ scrollMarginTop: `${anchorOffsetPx}px` }"
      >
        <div class="divide-x text-base">
          <span
            v-if="articleGrades"
            class="pr-2"
            v-text="articleGrades"
          />
          <span
            v-if="articleSubject"
            class="px-2"
            v-text="articleSubject"
          />
          <span
            class="pl-2"
            v-text="t(`contentTypes.guide`)"
          />
        </div>
        <h1
          class="fluid-text-4xl mt-8 mb-4 font-extrabold"
          v-text="getTitle(article)"
        />
        <RichTextRenderer :text="article.intro" />
      </header>
      <ArticleContentSkeleton
        v-if="isLoadingTeacherContent"
        color="var(--color-orange-5)"
        class="au-teacher-article mt-14 px-5 py-1 last:mb-16"
      />
      <article
        v-else-if="article.body"
        class="au-teacher-article px-5 py-1 last:mb-16"
      >
        <RichTextRenderer :text="article.body" />
      </article>
      <article
        v-for="content in teacherContent"
        :id="slugify(getTitle(content))"
        :key="content.contentId"
        class="au-teacher-article px-5 py-1 last:mb-16"
        :style="{ scrollMarginTop: `${anchorOffsetPx}px` }"
      >
        <h2 v-text="getTitle(content)" />
        <TeacherSubitems
          v-if="isPackage(content)"
          :header="content"
        />
        <RichTextRenderer
          v-if="isArticleContent(content)"
          :text="content.body || ''"
        />
      </article>
    </div>
  </div>
</template>
